import React, { useMemo } from 'react';
import { Tooltip, Spin, Button } from 'antd';
import { DollarOutlined, EuroOutlined, SyncOutlined } from '@ant-design/icons';
import { useGetExchangeRatesQuery } from 'sama-shared';
import { useTheme } from '../tokens';

interface ExchangeRateWidgetProps {
  onSync?: () => void | Promise<unknown>;
  isSyncing?: boolean;
}

export const ExchangeRateWidget: React.FC<ExchangeRateWidgetProps> = ({
  onSync,
  isSyncing = false,
}) => {
  const { t, mode } = useTheme();
  const { data: exchangeRates, isLoading, isFetching, refetch } = useGetExchangeRatesQuery();

  const isBusy = isSyncing || isFetching;

  const handleSync = () => {
    if (isSyncing) return;
    if (onSync) {
      // La mutación invalida el tag 'Finance', lo que re-dispara
      // getExchangeRates solo. No se llama a refetch() para evitar doble GET.
      void onSync();
      return;
    }
    refetch();
  };

  const { usdRate, eurRate, latestDate } = useMemo(() => {
    if (!exchangeRates) return { usdRate: null, eurRate: null, latestDate: null };
    const usd = exchangeRates.find((r: any) => r.currencyCode === 'USD');
    const eur = exchangeRates.find((r: any) => r.currencyCode === 'EUR');
    const dateStr = usd?.createdAt || eur?.createdAt;
    const date = dateStr ? new Date(dateStr) : null;
    return { usdRate: usd, eurRate: eur, latestDate: date };
  }, [exchangeRates]);

  if (isLoading) return <Spin size="small" style={{ margin: '0 8px' }} />;
  if (!usdRate && !eurRate) return null;

  const formattedDate = latestDate
    ? latestDate.toLocaleString('es-VE', {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      })
    : 'Desconocida';

  const RateDisplay = ({ rate, label }: { rate: { rateValue: number }; label: string }) => (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, lineHeight: 1 }}>
      <span
        style={{
          color: t.textMuted,
          fontSize: 11,
          fontWeight: 500,
        }}
      >
        {label}
      </span>
      <span
        style={{
          color: t.textPrimary,
          fontSize: 13,
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
          letterSpacing: '-0.02em',
        }}
      >
        {rate.rateValue.toFixed(2)}
      </span>
      <span
        style={{
          color: t.textMuted,
          fontSize: 10,
          fontWeight: 600,
        }}
      >
        VES
      </span>
    </div>
  );

  const isDark = mode === 'dark';

  return (
    <Tooltip
      placement="bottom"
      title={
        <div style={{ padding: '2px 4px' }}>
          <div style={{ fontWeight: 600, color: isDark ? '#fff' : '#1a1a1a' }}>
            Tasa Oficial de Cambio (BCV)
          </div>
          <div
            style={{ fontSize: 11, opacity: 0.85, marginTop: 4, color: isDark ? '#ccc' : '#555' }}
          >
            {usdRate && <div>1 USD = {usdRate.rateValue.toFixed(2)} VES</div>}
            {eurRate && <div>1 EUR = {eurRate.rateValue.toFixed(2)} VES</div>}
          </div>
          <div
            style={{ fontSize: 11, opacity: 0.65, marginTop: 4, color: isDark ? '#aaa' : '#888' }}
          >
            Actualizado: {formattedDate}
          </div>
          <div style={{ fontSize: 10, color: t.primary, marginTop: 6 }}>
            {isSyncing ? 'Sincronizando con BCV…' : 'Haz clic para sincronizar con BCV'}
          </div>
        </div>
      }
    >
      <Button
        type="text"
        onClick={handleSync}
        loading={isSyncing}
        disabled={isSyncing}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          height: 36,
          padding: '0 12px',
          borderRadius: t.radiusSm,
          backgroundColor: t.primaryMuted,
          border: `1px solid ${t.border}`,
          transition: 'all 150ms cubic-bezier(0.23, 1, 0.32, 1)',
          cursor: isSyncing ? 'wait' : 'pointer',
        }}
        className="exchange-rate-widget"
      >
        {/* Status dot: verde en reposo, naranja pulsante mientras sincroniza */}
        <span
          style={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            backgroundColor: isSyncing ? t.warning : t.success,
            boxShadow: `0 0 6px ${isSyncing ? t.warning : t.success}`,
            flexShrink: 0,
            animation: isSyncing ? 'statusPulse 1s ease-in-out infinite' : undefined,
          }}
        />

        {/* Rates */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            opacity: isSyncing ? 0.6 : 1,
          }}
        >
          {usdRate && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <DollarOutlined style={{ color: t.primary, fontSize: 12 }} />
              <RateDisplay rate={usdRate} label="USD" />
            </div>
          )}

          {usdRate && eurRate && (
            <div
              style={{
                width: 1,
                height: 12,
                backgroundColor: t.border,
              }}
            />
          )}

          {eurRate && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <EuroOutlined style={{ color: t.info, fontSize: 12 }} />
              <RateDisplay rate={eurRate} label="EUR" />
            </div>
          )}
        </div>

        {isBusy && <SyncOutlined spin style={{ fontSize: 10, color: t.primary, marginLeft: 2 }} />}
      </Button>
    </Tooltip>
  );
};

export default ExchangeRateWidget;
